/**
 * Viewer sound preferences, remembered across visits.
 *
 * The browser will not let us autoplay with sound just because the viewer
 * unmuted last time (autoplay permission is per-document, and the persistent
 * part — Chrome's Media Engagement Index, Safari's per-site Auto-Play setting
 * — is the browser's to grant). What we CAN carry over is their intent and
 * their volume level, so the moment they unmute we restore exactly what they
 * had instead of slamming to 100%.
 */

const KEY = 'lp_sound_pref'

export interface SoundPref {
  /** The viewer has deliberately turned sound on at least once */
  unmuted: boolean
  /** Last volume they listened at (0–1), never 0 */
  volume: number
}

const DEFAULT: SoundPref = { unmuted: false, volume: 1 }

export function getSoundPref(): SoundPref {
  try {
    const raw = window.localStorage.getItem(KEY)
    if (!raw) return { ...DEFAULT }
    const parsed = JSON.parse(raw) as Partial<SoundPref>
    const volume = typeof parsed.volume === 'number' ? Math.min(1, Math.max(0.05, parsed.volume)) : 1
    return { unmuted: parsed.unmuted === true, volume }
  } catch {
    return { ...DEFAULT }
  }
}

export function setSoundPref(pref: SoundPref): void {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(pref))
  } catch {
    /* storage unavailable (private mode, blocked cookies) — non-fatal */
  }
}

/**
 * What the browser will permit for autoplay *before* we try it.
 * Chrome/Firefox implement getAutoplayPolicy; elsewhere we return 'unknown'
 * and fall back to attempt-audible-then-retry-muted.
 */
export type AutoplayPolicy = 'allowed' | 'allowed-muted' | 'disallowed' | 'unknown'

export function autoplayPolicy(): AutoplayPolicy {
  try {
    const nav = navigator as Navigator & {
      getAutoplayPolicy?: (type: string) => AutoplayPolicy
    }
    if (typeof nav.getAutoplayPolicy === 'function') {
      return nav.getAutoplayPolicy('mediaelement')
    }
  } catch {
    /* not supported */
  }
  return 'unknown'
}
